'use client';

import { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Center, Text, VStack } from '@chakra-ui/react';

import { AnimatedCounter, Button } from 'components';
import { usePageTextContent } from 'context/pageTextContent';
import { formatCurrency } from 'utils/helpers';

interface Props {
  activatedGiftcard: AdaptedGiftcard;
  onOpenGiftcard(): void;
  isDigital: boolean;
}

export const ActivatedGiftcard = (props: Props) => {
  const { activatedGiftcard, onOpenGiftcard, isDigital } = props;

  const [isCountFinished, setIsCountFinished] = useState(false);

  const { content } =
    usePageTextContent<SponsoredGiftcardActivationPageContent>();

  const {
    downloadGiftcardDescription,
    activatedGiftcardDescription,
    openGiftcardButton,
    downloadGiftcardButton,
  } = content;

  const giftcardButton = useMemo(() => {
    if (isDigital) return downloadGiftcardButton;

    return openGiftcardButton;
  }, []);

  const description = useMemo(() => {
    if (isDigital) return downloadGiftcardDescription;

    return activatedGiftcardDescription;
  }, []);

  const formatAmount = (value: number) => {
    return formatCurrency({
      amount: value,
      currency: activatedGiftcard.currency,
    });
  };

  const onCountEnd = () => {
    setIsCountFinished(true);
  };

  return (
    <Center h="full" w="full" data-testid="giftcard-activation-overlay">
      <VStack
        h="full"
        align="center"
        justify="center"
        spacing="2rem"
        maxW={['40.625rem', '40.625rem', '25.3rem']}
        px="3rem"
      >
        <AnimatedCounter
          value={activatedGiftcard.activation.amount / 100}
          formatValue={formatAmount}
          fontSize="4rem"
          fontWeight="bold"
          onCountEnd={onCountEnd}
        />

        <motion.div
          initial={{ opacity: 0 }}
          animate={isCountFinished ? { opacity: 1 } : undefined}
          transition={{ duration: 1 }}
        >
          <Text mb="2rem" textAlign="center">
            {description}
          </Text>

          <Button variant="network" size="lg" onClick={onOpenGiftcard}>
            {giftcardButton}
          </Button>
        </motion.div>
      </VStack>
    </Center>
  );
};
